/**
 * What happens once an estimate made in the 3D designer has been saved and
 * has a guid: the preview still's data URL is swapped for the picture's URL
 * (the server wrote it to /renders/<guid>/preview.jpg), and the room's
 * export (held in memory since Get Estimate — advanced/renderAsset.ts) is
 * uploaded for the render job when the save handed back an upload token.
 *
 * Best effort throughout: the estimate is saved whatever happens here.
 */

import type { EstimateOutput } from '../types';
import { takeRenderAsset } from '../components/designer/advanced/renderAsset';
import { clearAdvancedState } from '../components/designer/advanced/advancedStorage';
import { uploadRenderAsset } from './api';

export interface SaveResponseExtras {
  previewUrl?: string | null;
  render?: { jobId: number; version: number; uploadToken: string; expiresAt: string } | null;
}

export async function afterEstimateSaved(
  guid: string,
  result: EstimateOutput | null,
  response: SaveResponseExtras | null | undefined,
  setPreviewImage: (image: string) => void,
): Promise<void> {
  if (!result?.designer) return;
  // A saved estimate ends the Advanced session: the next one starts with a
  // blank floor planner. "Back to Edit" re-seeds the designer from the
  // estimate itself (EstimateResults handleGoBack).
  clearAdvancedState();
  if (response?.previewUrl) setPreviewImage(response.previewUrl);
  const token = response?.render?.uploadToken;
  if (!token) return;
  const asset = takeRenderAsset(result.preview?.assetKey);
  if (!asset) return;
  try {
    await uploadRenderAsset(guid, token, asset);
  } catch {
    // The render is a bonus: the estimate is saved and shown regardless.
  }
}
